import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Note, Panel, ServerPage } from "@/components/server-shell";
import { ChoiceGroup, Chip, Select } from "@/components/ui-bits";
import { sealFor, useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/claude")({
  component: ClaudeTab,
});

type Status = {
  has_key: boolean;
  key_hint: string | null;
  claude_login: boolean;
  account: { logged_in: boolean; method: string | null; email: string | null; plan: string | null };
  auth_mode: "subscription" | "api_key";
  chat_auth?: "subscription" | "api_key";
  login_step: string | null;
  mode: "suggest" | "apply";
  models: { default: string; cycle: string; sweep: string; inbox: string; chat?: string };
  cost: { days: number; usd: number; runs: number };
};

const JOBS: [keyof Status["models"], string, string][] = [
  ["default", "Default", "Used by every job that has no model of its own."],
  ["cycle", "15-minute cycle", "Runs about 96 times a day; the biggest share of the cost."],
  ["sweep", "Morning sweep", "Once a day, reviews everything; worth the strongest model."],
  ["inbox", "Inbox scan", "Suggests newsletters and spam to clean up."],
  ["chat", "Assistant", "The chat. Used from your next message; the conversation carries on."],
];

function ClaudeTabBody() {
  const { id } = Route.useParams();
  const server = useServer(id).data;
  const connected = server?.hub.connected;
  const status = useAgentQuery<Status>(id, "claude_status", {}, !!connected);
  const [key, setKey] = useState("");
  const saveKey = useAgentAction<string>(
    id,
    "claude_set_key",
    async (k) => ({ key: await sealFor(server!.server, "anthropic-api-key", k) }),
    { refresh: ["claude_status"], success: "API key checked with Anthropic and saved on your server" },
  );
  const clearKey = useAgentAction<void>(id, "claude_set_key", () => ({ clear: true }), {
    refresh: ["claude_status"],
    success: "API key removed from your server",
  });
  const settings = useAgentAction<Record<string, unknown>>(id, "claude_settings", (v) => v, {
    refresh: ["claude_status"],
    success: "Saved; the next run uses it",
  });

  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const loginBegin = useAgentAction<void, { url: string }>(id, "claude_login_begin", () => ({}), {});
  const loginCode = useAgentAction<string, { account: { email: string } }>(
    id,
    "claude_login_code",
    async (c) => ({ code: await sealFor(server!.server, "claude-login-code", c) }),
    { refresh: ["claude_status"], success: (r) => `Signed in to Claude as ${r.account.email}` },
  );
  const logout = useAgentAction<void>(id, "claude_logout", () => ({}), { refresh: ["claude_status"], success: "Signed out of Claude" });

  if (!connected) return <NotConnected />;
  const st = status.data;
  if (status.error) return <p className="text-sm text-[#F48A80]">{status.error.message}</p>;
  if (!st) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const sub = st.auth_mode === "subscription";
  const chatSub = st.chat_auth === "subscription";

  return (
    <>
      <Panel title="What it may do in Plane">
        <ChoiceGroup
          label="Plane access"
          value={st.mode}
          disabled={settings.isPending}
          onChange={(mode) => settings.mutate({ mode })}
          options={[
            { value: "suggest", title: "Suggest only", body: "Each cycle proposes changes in Suggestions and changes nothing in Plane until you approve." },
            { value: "apply", title: "Apply changes", body: "Each cycle creates and updates tasks itself. Your rules and the guard still apply." },
          ]}
        />
        <Note>Switch to Apply once its suggestions look right. The morning sweep always applies.</Note>
      </Panel>

      <Panel title="How it pays for Claude">
        <ChoiceGroup
          label="Billing"
          value={st.auth_mode}
          disabled={settings.isPending}
          onChange={(auth_mode) => settings.mutate({ auth_mode })}
          options={[
            {
              value: "subscription",
              title: "My Claude subscription",
              body: "Your Pro or Max plan, signed in on your server. An always-on assistant uses a fair share of it; Max is the safer choice.",
            },
            { value: "api_key", title: "An API key", body: "Billed per use to your own Anthropic Console account." },
          ]}
        />

        {(sub || chatSub) && (
          <div className="flex flex-col gap-3 rounded-md bg-[#111111] px-4 py-3.5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">Claude subscription</span>
                {st.claude_login ? (
                  <span className="text-[13px] text-[#7FD39B]">
                    Signed in{st.account.email ? ` as ${st.account.email}` : ""}
                    {st.account.plan ? ` · ${st.account.plan}` : ""}
                  </span>
                ) : (
                  <span className="text-[13px] text-[#F48A80]">Not signed in</span>
                )}
              </div>
              {!loginUrl && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={st.claude_login ? "outline" : "default"}
                    disabled={loginBegin.isPending}
                    onClick={() => loginBegin.mutate(undefined, { onSuccess: (r) => setLoginUrl(r.url) })}
                  >
                    {loginBegin.isPending ? "Starting…" : st.claude_login ? "Use another account" : "Sign in with Claude"}
                  </Button>
                  {st.claude_login && (
                    <Button variant="ghost" disabled={logout.isPending} onClick={() => window.confirm("Sign this server out of Claude?") && logout.mutate()}>
                      Sign out
                    </Button>
                  )}
                </div>
              )}
            </div>
            {loginUrl && (
              <div className="flex flex-col gap-3 border-t border-[#2A2927] pt-3">
                <ol className="m-0 flex list-decimal flex-col gap-1.5 pl-5 text-[13px] text-[#CFCBC1]">
                  <li>
                    <a className="font-medium" href={loginUrl} target="_blank" rel="noreferrer">
                      Open Claude's sign-in page ↗
                    </a>{" "}
                    (claude.com), sign in with the account whose plan you want to use, and approve.
                  </li>
                  <li>Claude then shows a code. Paste it here; it goes encrypted to your server, which finishes the sign-in.</li>
                </ol>
                <form
                  className="flex flex-wrap gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    loginCode.mutate(code.trim(), {
                      onSuccess: () => {
                        setLoginUrl(null);
                        setCode("");
                      },
                    });
                  }}
                >
                  <Input
                    className="h-11 flex-[1_1_280px] font-mono"
                    autoComplete="off"
                    placeholder="Code from Claude"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                  />
                  <Button type="submit" size="lg" disabled={loginCode.isPending}>
                    {loginCode.isPending ? "Signing in…" : "Finish"}
                  </Button>
                  <Button type="button" size="lg" variant="ghost" onClick={() => setLoginUrl(null)}>
                    Cancel
                  </Button>
                </form>
              </div>
            )}
          </div>
        )}
        {(sub || chatSub) && <Note>The sign-in happens on Claude's own page and in Claude Code on your server. This panel never sees it.</Note>}
      </Panel>

      <Panel
        title="Assistant"
        badge={
          chatSub ? (
            <Chip value={st.claude_login ? "ok" : "error"} label={st.claude_login ? "subscription" : "not signed in"} />
          ) : (
            <Chip value={st.has_key ? "ok" : "error"} label={st.has_key ? "API key" : "no API key"} />
          )
        }
      >
        <ChoiceGroup
          label="The Assistant runs with"
          value={st.chat_auth ?? "api_key"}
          disabled={settings.isPending}
          onChange={(chat_auth) => settings.mutate({ chat_auth })}
          options={[
            {
              value: "subscription",
              title: "My Claude subscription",
              body: "Claude Code on your server, signed in with your own Pro or Max plan. Counts toward your plan's usage limits.",
            },
            { value: "api_key", title: "An API key", body: "Your Anthropic API key below, billed per use to your Anthropic Console account." },
          ]}
        />
        {chatSub && !st.claude_login && <Note>Sign in with your Claude subscription first, under "How it pays for Claude" above.</Note>}
        {!chatSub && !st.has_key && <Note>Add an API key below, or switch the Assistant to your subscription.</Note>}
        <Note>Either way you approve every change and command it makes. Its model is under Models → Assistant.</Note>
      </Panel>

      <Panel
        title="Anthropic API key"
        badge={st.has_key ? <Chip value="ok" label={`set · …${st.key_hint}`} /> : <Chip value={sub ? "neutral" : "error"} label="not set" />}
      >
        <p className="m-0 text-[#CFCBC1]">
          {sub
            ? chatSub
              ? "Not needed: your scheduled jobs and the Assistant both use your subscription."
              : "Only needed for the Assistant while it's set to use an API key. Scheduled jobs keep using your subscription."
            : "Fieldwork runs Claude Code on your server with this key, billed to your Anthropic account."}
        </p>
        <form
          className="flex flex-wrap items-end gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            saveKey.mutate(key, { onSuccess: () => setKey("") });
          }}
        >
          <label className="flex flex-[1_1_320px] flex-col gap-1.5">
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{st.has_key ? "Replace the key" : "API key"}</span>
            <Input
              className="h-11 px-3.5 font-mono"
              type="password"
              autoComplete="off"
              placeholder="sk-ant-…"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              required
            />
          </label>
          <Button type="submit" size="lg" disabled={saveKey.isPending}>
            {saveKey.isPending ? "Checking…" : "Check and save"}
          </Button>
          {st.has_key && (
            <Button
              type="button"
              size="lg"
              variant="ghost"
              className="text-[#F48A80] hover:text-[#F48A80]"
              disabled={clearKey.isPending}
              onClick={() =>
                window.confirm(
                  sub
                    ? chatSub
                      ? "Remove the API key? Your jobs and the Assistant keep using your subscription."
                      : "Remove the API key? The Assistant stops working until you set it to your subscription; your jobs keep using the subscription."
                    : "Remove the API key? Fieldwork stops working until a new one is set.",
                ) && clearKey.mutate()
              }
            >
              Remove
            </Button>
          )}
        </form>
        <Note>
          Encrypted in this browser to your server's key and stored only there. Create one at{" "}
          <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
            console.anthropic.com → API keys
          </a>
          .
        </Note>
      </Panel>

      <section
        aria-labelledby="ai-use"
        className="flex flex-wrap items-center gap-x-8 gap-y-5 rounded-md bg-[#1A1A19] px-6 py-[22px] shadow-[inset_0_0_0_1px_#2A2927]"
      >
        <div className="flex flex-col gap-1">
          <h2 id="ai-use" className="m-0 font-mono text-base font-bold">
            Usage · last {st.cost.days} days
          </h2>
          <span className="font-mono text-[32px] font-bold">${st.cost.usd.toFixed(2)}</span>
          <span className="text-[13px] text-[#A8A59D]">
            over {st.cost.runs} runs{sub ? " · API-equivalent; covered by your subscription" : ""}
          </span>
        </div>
        <p className="m-0 flex-[1_1_320px] text-[13px] text-[#CFCBC1]">
          Each run reports its own cost; exact billing is in the Anthropic console. A lighter model for the 15-minute cycle is
          the biggest saving.
        </p>
      </section>

      <Panel title="Models" className="gap-4">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-[18px]">
          {JOBS.map(([job, label, hint]) => (
            <label key={job} className="flex flex-col gap-1.5">
              <span className="font-medium">{label}</span>
              <Select
                className="h-11"
                value={st.models[job] ?? ""}
                disabled={settings.isPending}
                onChange={(e) => settings.mutate({ models: { [job]: e.target.value } })}
              >
                <option value="">{job === "default" ? "Opus · built-in default" : "Same as default"}</option>
                <option value="fable">Fable · newest, most capable</option>
                <option value="opus">Opus · strong</option>
                <option value="sonnet">Sonnet · balanced</option>
                <option value="haiku">Haiku · fastest, cheapest</option>
              </Select>
              <span className="text-xs text-[#A8A59D]">{hint}</span>
            </label>
          ))}
        </div>
        <Note>Saved as you change them; the next run uses them.</Note>
      </Panel>
    </>
  );
}

function ClaudeTab() {
  return (
    <ServerPage narrow eyebrow="Settings" title="AI & cost">
      <ClaudeTabBody />
    </ServerPage>
  );
}
